import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Shown by ProtectedRoute when an authenticated merchant returns from Stripe
 * checkout but isn't `isOnboarded` yet — the webhook that writes
 * subscription_tier can lag the browser redirect by a few seconds. We poll
 * /merchants/me until the tier lands (then ProtectedRoute re-renders and shows
 * the app), instead of bouncing a merchant who just paid back to onboarding.
 */
export default function CheckoutFinalizing() {
  const { reloadMerchant } = useAuth();
  const [tooLong, setTooLong] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const started = Date.now();

    const tick = async () => {
      if (cancelled) return;
      try {
        await reloadMerchant();
      } catch {
        // keep polling — a transient error shouldn't abort finalizing
      }
      if (cancelled) return;
      if (Date.now() - started > 45_000) {
        // Give up polling. Drop the pending flag so the merchant isn't trapped
        // on this screen — e.g. if they abandoned the Stripe page and came
        // back, the next gate evaluation routes them to onboarding instead.
        sessionStorage.removeItem("prep_checkout_pending");
        setTooLong(true);
        return;
      }
      window.setTimeout(tick, 2500);
    };

    const first = window.setTimeout(tick, 1200);
    return () => {
      cancelled = true;
      window.clearTimeout(first);
    };
  }, [reloadMerchant]);

  // The pending flag is cleared by ProtectedRoute once the merchant is
  // onboarded — this component unmounts on that same render, so an effect
  // here would never see isOnboarded=true.

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      {!tooLong ? (
        <>
          <p className="text-lg font-semibold text-foreground">
            Finalizing your account…
          </p>
          <p className="text-sm text-muted-foreground max-w-sm">
            Confirming your checkout with Stripe and activating your trial.
            This only takes a few seconds.
          </p>
        </>
      ) : (
        <>
          <p className="text-lg font-semibold text-foreground">
            Almost there
          </p>
          <p className="text-sm text-muted-foreground max-w-sm">
            This is taking longer than usual. If you completed payment, refresh
            in a moment — otherwise head back to finish setup.
          </p>
          <div className="flex gap-2 mt-2">
            <Button onClick={() => window.location.reload()}>Refresh</Button>
            <Button
              variant="outline"
              onClick={() => { window.location.href = "/onboarding/step/activate"; }}
            >
              Back to setup
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
